import {
  useContext, useEffect, useMemo, useState,
} from 'react';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { Feature, Map, MapBrowserEvent } from 'ol';
import {
  LineString, Point, Circle as CircleGeometry,
} from 'ol/geom';
import {
  Circle, Fill, Stroke, Style,
} from 'ol/style';
import {
  Draw,
} from 'ol/interaction';
import colors from 'configs/colors';
import { Coordinate } from 'ol/coordinate';
import { Pixel } from 'ol/pixel';
import { Projection, get, transform } from 'ol/proj';
import { availableProjections } from 'configs/projections';
import MainAppContext from '../../MainAppContext';
import { getLayerByName } from './utils';
import CrossSectionContext from '../../CrossSectionContext';
import LayerName from './enums';
import LineStringInteractionUtils from './useLineStringInteraction.utils';

function useLineStringInteraction(map: Map) {
  const { crossSectionMode, currentProjection } = useContext(MainAppContext);
  const {
    setShowCrossSectionModal,
    setSegment,
    setMousePosition,
    mousePosition,
    lastMousePositionUpdateOrigin,
    setLastMousePositionUpdateOrigin,
  } = useContext(CrossSectionContext);
  const [line, setLine] = useState<Draw>();
  const [isModifying, setIsModifying] = useState<boolean>(false);
  const [isTranslating, setIsTranslating] = useState<boolean>(false);

  const utils = useMemo(() => new LineStringInteractionUtils(map), [map]);

  useEffect(() => {
    const calculateMousePosition = (a: Pixel, b: Pixel, lineString: LineString) => {
      const pixelDistance = utils.getPixelDistance(a, b);
      const lineLength = lineString.getLength();
      const lineLengthPixels = lineLength / (map.getView().getResolution() || 1);
      const mousePercentage = (pixelDistance / lineLengthPixels) * 100;
      return Math.round(mousePercentage);
    };

    const handleInteractions = ({
      eventPixel,
      layer,
      coordinates,
    } : {
      layer: VectorLayer<VectorSource>,
      eventPixel: Pixel,
      coordinates: Coordinate[]
    }, lineString : LineString) => {
      const cursorPixel = eventPixel;
      const source = layer.getSource() as VectorSource;

      const firstTargetPixel = map.getPixelFromCoordinate(coordinates[0]);
      const lastTargetPixel = map.getPixelFromCoordinate(coordinates[1]);

      if (firstTargetPixel && lastTargetPixel) {
        const firstDistanceInPixels = utils.getPixelDistance(cursorPixel, firstTargetPixel);
        const lastDistanceInPixels = utils.getPixelDistance(cursorPixel, lastTargetPixel);

        if (firstDistanceInPixels <= 10 || lastDistanceInPixels <= 10) {
          if (!utils.isInteraction('modifyInteraction')) {
            utils.cleanInteractions();
            map.addInteraction(utils.generateModifyInteraction(
              source,
              setIsModifying,
              setSegment,
            ));
          }
        } else if (!utils.isInteraction('translateInteraction') && !isModifying) {
          utils.cleanInteractions();
          const translateInteraction = utils.generateTranslateInteraction(
            layer,
            setIsTranslating,
            setSegment,
          );

          map.addInteraction(translateInteraction);
        }
      }
    };

    const onPointerMove = (event: MapBrowserEvent<UIEvent>) => {
      const { pixel } = event;
      let selectedLayer;
      let selectedSource;

      const feature = map.forEachFeatureAtPixel(pixel, (feature, layer) => {
        if (layer?.get('name') === LayerName.CrossSectionLayer) {
          selectedLayer = layer as VectorLayer<VectorSource>;
          selectedSource = layer.getSource() as VectorSource;
          return feature;
        }
        return false;
      });

      if (feature && selectedLayer && selectedSource) {
        const lineGeometry = feature.getGeometry() as LineString;
        const firstCoordinate = lineGeometry.getFirstCoordinate();
        const lastCoordinate = lineGeometry.getLastCoordinate();

        const pointerCoordinates = event.coordinate;

        const firstPixel = map.getPixelFromCoordinate(firstCoordinate);
        const pointerPixel = map.getPixelFromCoordinate(pointerCoordinates);

        if (firstPixel && pointerPixel) {
          const mousePosition = calculateMousePosition(pointerPixel, firstPixel, lineGeometry);
          setMousePosition(mousePosition);
          setLastMousePositionUpdateOrigin('map');
        }
        handleInteractions({
          eventPixel: pixel,
          coordinates: [firstCoordinate, lastCoordinate],
          layer: selectedLayer,
        }, lineGeometry);
      } else if (mousePosition !== 0 && !isTranslating) {
        const layer = map.getAllLayers().find((layer) => layer.get('name') === LayerName.CrossSectionLayer) as VectorLayer<VectorSource>;
        const source = layer?.getSource() as VectorSource;
        setMousePosition(0);
        setLastMousePositionUpdateOrigin('map');
        if (selectedSource) {
          utils.cleanInteractions();
          map.addInteraction(utils.generateDrawInteraction(
            source,
            setShowCrossSectionModal,
            setSegment,
            setLine,
          ));
        }
      } else if (!utils.isInteraction(LayerName.CrossSectionLayer) && map && !isTranslating) {
        const layer = map.getAllLayers().find((layer) => layer.get('name') === LayerName.CrossSectionLayer) as VectorLayer<VectorSource>;
        const source = layer?.getSource() as VectorSource;

        if (source) {
          map.addInteraction(utils.generateDrawInteraction(
            source,
            setShowCrossSectionModal,
            setSegment,
            setLine,
          ));
        }
      }
    };
    if (line)map.on('pointermove', onPointerMove);

    return () => { map.un('pointermove', onPointerMove); };
  }, [
    map, mousePosition, setMousePosition,
    isModifying, isTranslating, line,
    setLastMousePositionUpdateOrigin, utils,
    setSegment, setShowCrossSectionModal]);

  useEffect(() => {
    const addInteraction = () => {
      const shadowLayer = utils.generateShadowLayer();
      const vectorLayer = utils.generateVectorLayer();
      const drawInteraction = utils.generateDrawInteraction(
        vectorLayer?.getSource() as VectorSource,
        setShowCrossSectionModal,
        setSegment,
        setLine,
      );
      const trackingLayer = utils.generateTrackingLayer();

      map.addLayer(shadowLayer);
      map.addLayer(vectorLayer);
      map.addLayer(trackingLayer);

      map.addInteraction(drawInteraction);
    };
    if (map && crossSectionMode && !line) {
      addInteraction();
    }
  }, [crossSectionMode, line, map, setSegment, setShowCrossSectionModal, utils]);

  useEffect(() => {
    if (!crossSectionMode && line && map) {
      const crossSectionlayer = getLayerByName(
        map,
        LayerName.CrossSectionLayer,
      ) as VectorLayer<VectorSource>;
      const shadowLayer = getLayerByName(
        map,
        LayerName.ShadowLayer,
      ) as VectorLayer<VectorSource>;
      const trackingLayer = getLayerByName(
        map,
        LayerName.TrackingLayer,
      ) as VectorLayer<VectorSource>;
      if (crossSectionlayer && shadowLayer) {
        map.dispatchEvent('click');
        utils.cleanInteractions();

        map.removeLayer(crossSectionlayer);
        map.removeLayer(shadowLayer);
        if (trackingLayer) {
          map.removeLayer(trackingLayer);
        }

        setLine(undefined);
        setMousePosition(0);
        setLastMousePositionUpdateOrigin('map');
        setShowCrossSectionModal(false);
      }
    }
  }, [
    crossSectionMode, line, map,
    setLastMousePositionUpdateOrigin,
    setMousePosition, setShowCrossSectionModal,
    utils]);

  useEffect(() => {
    if (lastMousePositionUpdateOrigin === 'chart') {
      const layer = getLayerByName(
        map as Map,
        LayerName.CrossSectionLayer,
      ) as VectorLayer<VectorSource>;
      const trackingLayer = getLayerByName(
        map as Map,
        LayerName.TrackingLayer,
      ) as VectorLayer<VectorSource>;
      const trackingLayerSource = trackingLayer?.getSource();
      const source = layer?.getSource() as VectorSource;
      const feature = source?.getFeatures().at(0) as Feature<LineString>;
      const trackingFeature = trackingLayerSource?.getFeatures().at(0) as Feature<CircleGeometry>;
      const projection = map.getView().getProjection();

      if (feature && trackingFeature && mousePosition !== undefined) {
        trackingLayer.setStyle((feature) => {
          const coords = feature.get('coords');
          return [
            new Style({
              geometry: new Point(coords),
              image: new Circle({
                fill: new Fill({
                  color: 'white',
                }),
                stroke: new Stroke({
                  color: colors.primary,
                  width: 2,
                }),
                radius: 4,
              }),
            }),
          ];
        });
        const lineString = feature.getGeometry();
        const point = lineString?.getCoordinateAt(mousePosition / 100);
        if (point && projection) {
          trackingFeature.set('coords', point);
          trackingFeature.getGeometry()?.setCoordinates(
            transform(
              point || [0, 0],
              get(availableProjections.find(
                (projection) => projection.name === currentProjection,
              )?.epsg as string) as Projection,
              projection as Projection,
            ),
            trackingFeature.getGeometry()?.getLayout(),
          );
        }
      }
    }
  }, [map, mousePosition, lastMousePositionUpdateOrigin, currentProjection]);

  useEffect(() => {
    if (lastMousePositionUpdateOrigin === 'chart') {
      map.addLayer(utils.generateTrackingLayer());
    } else {
      const trackingLayer = getLayerByName(
        map as Map,
        LayerName.TrackingLayer,
      ) as VectorLayer<VectorSource>;
      if (trackingLayer) { map.removeLayer(trackingLayer); }
    }
  }, [lastMousePositionUpdateOrigin, map, utils]);

  return { line };
}

export default useLineStringInteraction;
